import React from "react";
import * as Yup from "yup";
import { Formik, Form, FieldArray } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import Input from "./form/Input";

const createValidationSchema = isDecimalField => {
  if (isDecimalField) {
    return Yup.object({
      options: Yup.array().of(
        Yup.number()
          .required("Option is required")
          .typeError("Option must be a valid decimal number")
      ),
    });
  }

  return Yup.object({
    options: Yup.array().of(
      Yup.string()
        .required("Option is required")
        .min(1, "Option cannot be empty")
    ),
  });
};

export default function DropdownMenuOptionsDialog({
  onClose,
  onSave,
  initialOptions = [],
  defaultValue,
  onOpenDefaultValueDialog,
  fieldType,
}) {
  const isDecimalField = fieldType === "decimal";

  const handleSubmit = values => {
    if (isDecimalField) {
      const validOptions = values.options
        .map(option => {
          const num = parseFloat(option);
          return !isNaN(num) ? num.toString() : null;
        })
        .filter(option => option !== null);
      onSave(validOptions);
    } else {
      onSave(values.options.filter(option => option.trim() !== ""));
    }
    onClose();
  };

  const getInitialValues = () => {
    if (isDecimalField) {
      return {
        options: initialOptions.length > 0 ? initialOptions.map(opt => Number(opt)) : [0],
      };
    }
    return {
      options: initialOptions.length > 0 ? initialOptions : [""],
    };
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", textAlign: "center" }}>
        <Typography fontSize={22} fontWeight={500} color="textPrimary" align="center">
          Dropdown Menu Options
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={createValidationSchema(isDecimalField)}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched }) => {
          const hasValidOptions = isDecimalField
            ? values.options.some(option => !isNaN(parseFloat(option)) && isFinite(option))
            : values.options.some(option => option.trim() !== "");

          const openAdvancedOptions = () => {
            handleSubmit(values);
            onOpenDefaultValueDialog();
          };

          return (
            <Form>
              <DialogContent>
                <FieldArray name="options">
                  {({ remove, push }) => (
                    <Box>
                      {values.options.length > 0 && (
                        <Grid container spacing={2}>
                          {values.options.map((option, index) => (
                            <Grid key={index} size={12}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Input
                                    name={`options.${index}`}
                                    label={
                                      option && defaultValue === option
                                        ? `Option ${index + 1} (Default Value)`
                                        : `Option ${index + 1}`
                                    }
                                    size="medium"
                                    fullWidth
                                    showNAButton={false}
                                    type={isDecimalField ? "number" : undefined}
                                    step={isDecimalField ? "0.01" : undefined}
                                    error={touched.options?.[index] && errors.options?.[index]}
                                    helperText={touched.options?.[index] && errors.options?.[index]}
                                  />
                                </Box>
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => remove(index)}
                                  sx={{ mt: 0.5 }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => push(isDecimalField ? 0 : "")}
                        size="large"
                        sx={{ mt: 2 }}
                      >
                        Add More
                      </Button>
                    </Box>
                  )}
                </FieldArray>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Button variant="text" color="primary" onClick={openAdvancedOptions}>
                    Advanced Options
                  </Button>
                  <Box display="flex" gap={1}>
                    <Button onClick={onClose} variant="outlined">
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                      Save
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
